import { createHOC } from 'vue-hoc';
import isEqual from 'lodash/isEqual';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import gql from 'graphql-tag';
import Promise from 'bluebird';
import withUser from '~lib/common/hoc/withUser';

import auth from '~lib/common/providers/auth';
import casesService from '~lib/common/providers/casesService';

import CaseCopyTool from './CaseCopyTool';
import removeUncomparableCaseFields from '../CaseDiffTool/removeUncomparableCaseFields';
import parseGroupMetadata from '../../../utils/parseGroupMetadata';
import envBaseUrl from '../../../utils/envBaseUrl';
import envUsersService from '../../../utils/envUsersService';

const SIM_CASE_DECRYPTION_CODE = 'obuicX9hPVJ1NlQyepu9lg==';

const fetchSimCase = async (env, caseId, token, label) => {
  const response = await fetch(
    `${envBaseUrl(env)}/api/cases/${caseId}?code=${SIM_CASE_DECRYPTION_CODE}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (response.ok) {
    return response.json();
  }
  const data = await response.json();
  throw new Error(`Error when fetching case in ${label} environment: ${data && data.errorInfo && data.errorInfo.error || 'unknown error'}`);
};

// Every audio file the sim will request for this case. Patients, npcs and
// callees each expose answers carrying an audioClip, and these urls come from
// the same resolvers that serve the sim — so this is what the sim will actually
// ask for, not a re-derivation of it.
const collectAudioUrls = (simCase) =>
  uniq(
    flatten([
      ...(simCase.patients || []).map((patient) => patient.answers || []),
      ...(simCase.npcs || []).map((npc) => npc.answers || []),
      ...(simCase.callees || []).map((callee) => callee.answers || []),
    ])
      .filter((answer) => answer && answer.audioClip && answer.audioClip.mp3)
      .map((answer) => answer.audioClip.mp3)
  );

const options = {
  name: 'CaseCopyToolContainer',
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: {
        loading: false,
        copying: false,
        fromEnv: 'dev',
        toEnv: 'dev',
        error: null,
        audioReports: [],
      },
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
  },
  methods: {
    // Copies the dialogue audio the source environment generated into the target
    // environment's own S3 path prefix. Audio keys are namespaced per
    // environment, so without this a copied case points at files that do not
    // exist on the other side.
    //
    // Deliberately never fatal. The case itself is already correct in the
    // database, and retrying this is idempotent and cheap, whereas the caller's
    // error path deletes every case in the batch — far too much to lose over one
    // transient S3 error, or over a target environment that has not been
    // deployed with this mutation yet.
    async copyAudioForCase(caseId, restoredCaseId, sourceCaseJson, toEnv) {
      const audioUrls = collectAudioUrls(sourceCaseJson);
      if (!audioUrls.length) {
        return;
      }
      try {
        const response = await casesService.mutate({
          mutation: gql`
            mutation copyCaseAudio($audioUrls: [String]) {
              copyCaseAudio(audioUrls: $audioUrls)
            }
          `,
          variables: {
            audioUrls,
          },
          context: {
            uri: `${envBaseUrl(toEnv)}/graphql`,
          },
        });
        this.state.audioReports.push({
          caseId,
          restoredCaseId,
          ...response.data.copyCaseAudio,
        });
      } catch (e) {
        console.error('Copying case audio failed', e);
        this.state.audioReports.push({
          caseId,
          restoredCaseId,
          total: audioUrls.length,
          error: (e && e.message) || 'unknown error',
        });
      }
    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    copying() {
      return this.state.copying;
    },
    error() {
      return this.state.error;
    },
    audioReports() {
      return this.state.audioReports;
    },
  },
  listeners: {
    updateFromEnv(env) {
      this.state.fromEnv = env;
    },
    updateToEnv(env) {
      this.state.toEnv = env;
    },
    async copy(selectedCaseIds, fromOrg, toOrg, fromUser, toUser, ignoreDiffs = false) {
      const casesCopiedSoFar = [];
      const { fromEnv, toEnv } = this.state;
      try {
        this.state.copying = true;
        this.state.error = null;
        this.state.audioReports = [];

        // Hoisted above the copy: the source case json is now read during the
        // copy itself, not only during verification.
        const fromUserToken = (
          await envUsersService(fromEnv).loginAs(fromOrg._id, fromUser.email)
        ).idToken;

        const toUserToken = (
          await envUsersService(toEnv).loginAs(toOrg._id, toUser.email)
        ).idToken;

        await Promise.map(
          selectedCaseIds,
          async (selectedCaseId) => {
            const dumpResponse = await casesService.mutate({
              mutation: gql`
                mutation dumpCase($caseId: String) {
                  dumpCase(caseId: $caseId)
                }
              `,
              variables: {
                caseId: selectedCaseId,
              },
              context: {
                uri: `${envBaseUrl(fromEnv)}/graphql`,
              },
            });
            const caseDump = dumpResponse.data.dumpCase;
            const toMetadata = parseGroupMetadata(toOrg);
            const toOrgAccess = {
              run: { users: [toUser.user_id] },
              read: { users: [toUser.user_id] },
              write: { users: [toUser.user_id] },
              share: { users: [toUser.user_id] },
              owner: toUser.user_id,
              ownerOrg: toOrg._id,
              productType: toMetadata.productType || 'EMS',
            };
            if (!caseDump.newCase) {
              return;
            }
            caseDump.newCase.access = toOrgAccess;
            // If the target environment does not have the new avatarKey field
            // read it from the old caseConfigurations_avatar relation
            if (!caseDump.newCase.avatarKey) {
              const avatarRelation = caseDump.relationMap.caseConfigurations_avatar[0];
              caseDump.newCase.avatarKey = avatarRelation ? avatarRelation._to.split('/')[1] : undefined;
            }
            // Uncomment this to emulate case data diff after copy
            // caseDump.newCase.name = 'TEST - Wrong case name after copy';
            const restoreResponse = await casesService.mutate({
              mutation: gql`
                mutation restoreCase($caseDump: JSON) {
                  restoreCase(caseDump: $caseDump)
                }
              `,
              variables: {
                caseDump,
              },
              context: {
                uri: `${envBaseUrl(toEnv)}/graphql`
              }
            });
            const restoredCaseId = restoreResponse.data.restoreCase;
            casesCopiedSoFar.push(restoredCaseId);

            const dumpedCaseJson = await fetchSimCase(
              fromEnv,
              selectedCaseId,
              fromUserToken,
              'source'
            );

            // Before the target environment is asked for the case. Assembling it
            // reads each mp3 to size the dialogue wait timers, and a file that is
            // missing at that moment is remembered as missing for the life of the
            // process — so the audio has to be in place first, or the copy fails
            // verification on timers that have nothing to do with case content.
            await this.copyAudioForCase(
              selectedCaseId,
              restoredCaseId,
              dumpedCaseJson,
              toEnv
            );

            const restoredCaseJson = await fetchSimCase(
              toEnv,
              restoredCaseId,
              toUserToken,
              'target'
            );

            const comparableDumpedCaseJson = removeUncomparableCaseFields(
              dumpedCaseJson
            );
            const comparableRestoredCaseJson = removeUncomparableCaseFields(
              restoredCaseJson
            );
            if (!ignoreDiffs && !isEqual(comparableDumpedCaseJson, comparableRestoredCaseJson)) {
              const error = new Error(
                'Case data is not the same after copy. Please take a screenshot of the diff below and contact support.'
              );
              error.code = 'CASE_JSON_NOT_EQUAL';
              error.dumpedCaseJson = JSON.stringify(
                comparableDumpedCaseJson,
                null,
                2
              );
              error.restoredCaseJson = JSON.stringify(
                comparableRestoredCaseJson,
                null,
                2
              );
              throw error;
            }

            return restoredCaseId;
          },
          {
            // Throttle the copy a bit to prevent payload too large errors
            concurrency: 3,
          }
        );
      } catch (e) {
        console.error(e);
        if (e.graphqlErrors && e.graphqlErrors.length) {
          this.state.error = e.graphqlErrors[0];
        }
        this.state.error = e;
        await casesService.mutate({
          mutation: gql`
            mutation bulkRemoveCaseConfigurations($caseIds: [ID]) {
              bulkRemoveCaseConfigurations(ids: $caseIds)
            }
          `,
          variables: {
            caseIds: casesCopiedSoFar,
          },
          context: {
            uri: `${envBaseUrl(toEnv)}/graphql`
          }
        });
      } finally {
        this.state.copying = false;
      }
    },
  },
};

const CaseCopyToolContainer = createHOC(CaseCopyTool, options, renderWith);
export default withUser()(CaseCopyToolContainer);
